<script lang="ts" setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ScheduleList from "@/views/scheduler/components/ScheduleList.vue";
import ScheduleForm from "@/views/scheduler/components/ScheduleForm.vue";
import type { Project } from "@/types";

const props = defineProps<{ project: Project }>();
const projectId = props.project.id;
const route = useRoute();
const router = useRouter();

// Inline editor state: null = showing the list; "new" = creating; otherwise editing that schedule.
// Keeping it inside the tab means the user never leaves the project to manage its schedules.
const editingId = ref<string | null>(null);

// Deep-open support: the Workflows tab's "scheduled" indicator links here with ?open={scheduleId}
// when a workflow has a single schedule, so we open it directly.
onMounted(() => {
  const open = route.query.open;
  if (typeof open === "string" && open) editingId.value = open;
});

function clearOpenQuery() {
  if (route.query.open) router.replace({ query: { ...route.query, open: undefined } });
}

function createSchedule() {
  editingId.value = "new";
}
function editSchedule(id: string) {
  editingId.value = id;
}
function backToList() {
  editingId.value = null;
  clearOpenQuery();
}
</script>

<template>
  <div>
    <!-- List view (scoped to this project; no stats / filters / bulk — a project has few schedules) -->
    <template v-if="editingId === null">
      <div class="d-flex justify-content-end mb-3">
        <button class="bb-btn-primary-cta" @click="createSchedule">
          <i class="bi bi-plus me-2"></i>New Schedule
        </button>
      </div>
      <ScheduleList
        :project-id="projectId"
        :show-stats="false"
        :show-filters="false"
        :show-bulk="false"
        title="Schedules"
        @edit="editSchedule"
        @create="createSchedule"
      />
    </template>

    <!-- Inline create/edit (project is locked; on save/cancel/delete return to the list) -->
    <template v-else>
      <div class="d-flex justify-content-end mb-3">
        <button class="bb-btn-secondary-cta" @click="backToList">
          <i class="bi bi-arrow-left me-2"></i>Back to Schedules
        </button>
      </div>
      <ScheduleForm
        :schedule-id="editingId === 'new' ? null : editingId"
        :locked-project-id="projectId"
        @saved="backToList"
        @cancelled="backToList"
        @deleted="backToList"
      />
    </template>
  </div>
</template>
